import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../services/api';
import { mapCommitteeFromApi, mapCompanyFromApi } from '../../utils/apiMappers';
import { COMMITTEE_TYPES, normalizeCommitteeTypeCode } from '../../constants/committeeTypes';
import { useLookups } from '../../hooks/useLookups';

const parseQuorumFields = (quorum) => {
  if (!quorum) {
    return { quorumType: 'PERCENTAGE', quorumPercentage: 50, quorumNumber: '' };
  }
  const str = String(quorum);
  if (str.includes('%')) {
    return {
      quorumType: 'PERCENTAGE',
      quorumPercentage: parseInt(str, 10) || 50,
      quorumNumber: '',
    };
  }
  return {
    quorumType: 'NUMBER',
    quorumNumber: str,
    quorumPercentage: 50,
  };
};

const CreateCommittee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const prefillCompanyId = searchParams.get('companyId') || '';
  const isEditMode = Boolean(id);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [loadingCompanies, setLoadingCompanies] = useState(!isEditMode);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState('');
  const companyLocked = Boolean(prefillCompanyId) && !isEditMode;
  const { items: lookupCommitteeTypes } = useLookups('COMMITTEE_TYPE');

  const [formData, setFormData] = useState({
    companyId: prefillCompanyId,
    companyName: '',
    name: '',
    type: '',
    description: '',
    quorumType: 'PERCENTAGE',
    quorumNumber: '',
    quorumPercentage: 50,
    chairpersonRequiredForQuorum: false,
    meetingFrequency: '',
    charterDocument: null,
    reportsTo: '', // For governance chart
    reportingStructure: '',
    regulatoryRequirements: '',
  });

  const [errors, setErrors] = useState({});

  const navigateAfterSave = () => {
    if (!isEditMode && prefillCompanyId && formData.companyId) {
      navigate(`/companies/${formData.companyId}`);
      return;
    }
    navigate(isEditMode ? `/committees/${id}` : '/committees');
  };

  const handleCancel = () => {
    if (!isEditMode && formData.companyId && prefillCompanyId) {
      navigate(`/companies/${formData.companyId}`);
      return;
    }
    navigate(isEditMode ? `/committees/${id}` : '/committees');
  };

  useEffect(() => {
    if (isEditMode) return undefined;

    let cancelled = false;
    (async () => {
      try {
        setLoadingCompanies(true);
        const res = await csAdminApi.getCompanies();
        const list = (res.data?.data || [])
          .map(mapCompanyFromApi)
          .filter(Boolean);

        if (cancelled) return;

        setCompanies(list);

        const initialId = prefillCompanyId || formData.companyId;
        if (initialId) {
          const match = list.find((c) => String(c.id) === String(initialId));
          if (match) {
            setFormData((prev) => ({
              ...prev,
              companyId: String(match.id),
              companyName: match.name,
            }));
          }
        } else if (list.length === 1) {
          setFormData((prev) => ({
            ...prev,
            companyId: String(list[0].id),
            companyName: list[0].name,
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading companies:', err);
          setError('Failed to load companies');
        }
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, prefillCompanyId]);

  useEffect(() => {
    if (!isEditMode) {
      setFetching(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setFetching(true);
        setError('');
        const res = await csAdminApi.getCommitteeById(id);
        const data = mapCommitteeFromApi(res.data?.data);
        if (!data) {
          if (!cancelled) setError('Committee not found');
          return;
        }
        const quorumFields = parseQuorumFields(data.quorum);
        if (!cancelled) {
          setFormData((prev) => ({
            ...prev,
            companyId: data.companyId ? String(data.companyId) : '',
            companyName: data.company || '',
            name: data.name || '',
            type: data.typeCode || normalizeCommitteeTypeCode(data.type) || '',
            description: data.description || '',
            ...quorumFields,
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || 'Failed to load committee');
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === 'true',
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        charterDocument: file,
      }));
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    const match = companies.find((c) => String(c.id) === String(companyId));
    setFormData((prev) => ({
      ...prev,
      companyId,
      companyName: match?.name || '',
    }));
    if (errors.companyId) {
      setErrors((prev) => ({ ...prev, companyId: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isEditMode && !formData.companyId) {
      newErrors.companyId = 'Company is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Committee name is required';
    }

    if (!formData.type) {
      newErrors.type = 'Committee type is required';
    }

    if (formData.quorumType === 'NUMBER' && !formData.quorumNumber) {
      newErrors.quorumNumber = 'Quorum number is required';
    }

    if (formData.quorumType === 'PERCENTAGE' && !formData.quorumPercentage) {
      newErrors.quorumPercentage = 'Quorum percentage is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setError('');

      {
        const payload = {
          name: formData.name,
          type: normalizeCommitteeTypeCode(formData.type) || formData.type,
          quorumType: formData.quorumType,
          quorumNumber: formData.quorumNumber,
          quorumPercentage: formData.quorumPercentage,
          description: formData.description,
        };

        if (isEditMode) {
          await csAdminApi.updateCommittee(id, payload);
        } else {
          await csAdminApi.createCommittee({
            ...payload,
            companyId: Number(formData.companyId),
          });
        }
      }

      enqueueSnackbar(
        isEditMode ? 'Committee updated successfully' : 'Committee created successfully',
        { variant: 'success' }
      );
      navigateAfterSave();
    } catch (err) {
      console.error('Error creating committee:', err);
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} committee`);
      enqueueSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} committee`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const committeeTypes = lookupCommitteeTypes.length
    ? lookupCommitteeTypes.map((t) => ({ code: t.code, label: t.label }))
    : COMMITTEE_TYPES;

  const meetingFrequencies = [
    'Weekly',
    'Bi-weekly',
    'Monthly',
    'Quarterly',
    'Semi-annually',
    'Annually',
    'As needed',
  ];

  // Reports To options for governance chart
  const reportsToOptions = [
    { value: 'board', label: 'Board of Directors' },
    { value: 'chairman', label: 'Chairman of the Board' },
    { value: 'ceo', label: 'Chief Executive Officer (CEO)' },
    { value: 'audit_committee', label: 'Audit Committee' },
    { value: 'risk_committee', label: 'Risk Committee' },
    { value: 'executive_committee', label: 'Executive Committee' },
    { value: 'other_committee', label: 'Other Committee' },
  ];

  if (fetching || loadingCompanies) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Committee' : 'Create New Committee'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {isEditMode
            ? 'Update committee details and governance rules'
            : 'Set up a new committee with members, roles, and governance rules'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Basic Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              {isEditMode ? (
                <TextField
                  fullWidth
                  label="Company"
                  value={formData.companyName || '—'}
                  disabled
                  helperText="Committees are linked to a single company"
                />
              ) : (
                <FormControl fullWidth required error={!!errors.companyId}>
                  <InputLabel>Company</InputLabel>
                  <Select
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleCompanyChange}
                    label="Company"
                    disabled={loading || companyLocked}
                  >
                    <MenuItem value="">
                      <em>Select company</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={String(company.id)}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.companyId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.companyId}
                    </Typography>
                  )}
                  {!errors.companyId && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.75 }}>
                      {companyLocked
                        ? 'Company is fixed when creating from a company profile'
                        : 'Every committee must belong to a company'}
                    </Typography>
                  )}
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Committee Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name || 'e.g., Audit Committee, Risk Committee'}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.type}>
                <InputLabel>Committee Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {committeeTypes.map((type) => (
                    <MenuItem key={type.code} value={type.code}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Meeting Frequency</InputLabel>
                <Select
                  name="meetingFrequency"
                  value={formData.meetingFrequency}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {meetingFrequencies.map((freq) => (
                    <MenuItem key={freq} value={freq}>
                      {freq}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                helperText="Brief description of the committee's purpose and responsibilities"
                disabled={loading}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Quorum Requirements */}
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Quorum Requirements
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Quorum Type</FormLabel>
                <RadioGroup
                  row
                  name="quorumType"
                  value={formData.quorumType}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value="NUMBER"
                    control={<Radio />}
                    label="Number of members"
                    disabled={loading}
                  />
                  <FormControlLabel
                    value="PERCENTAGE"
                    control={<Radio />}
                    label="Percentage of total"
                    disabled={loading}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {formData.quorumType === 'NUMBER' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Minimum Members Required"
                  name="quorumNumber"
                  value={formData.quorumNumber}
                  onChange={handleChange}
                  error={!!errors.quorumNumber}
                  helperText={errors.quorumNumber || 'e.g., 3 members'}
                  disabled={loading}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            {formData.quorumType === 'PERCENTAGE' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Quorum Percentage"
                  name="quorumPercentage"
                  value={formData.quorumPercentage}
                  onChange={handleChange}
                  error={!!errors.quorumPercentage}
                  helperText={errors.quorumPercentage}
                  disabled={loading}
                  inputProps={{ min: 1, max: 100 }}
                  InputProps={{
                    endAdornment: '%',
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Chairperson Required for Quorum?</FormLabel>
                <RadioGroup
                  row
                  name="chairpersonRequiredForQuorum"
                  value={formData.chairpersonRequiredForQuorum.toString()}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Yes"
                    disabled={loading}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="No"
                    disabled={loading}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Committee Charter & Reporting */}
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Reporting Structure (for Governance Chart)
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Reports To</InputLabel>
                <Select
                  name="reportsTo"
                  value={formData.reportsTo}
                  onChange={handleChange}
                  label="Reports To"
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>Select reporting body</em>
                  </MenuItem>
                  {reportsToOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Reporting Details"
                name="reportingStructure"
                value={formData.reportingStructure}
                onChange={handleChange}
                helperText="Any additional details about the reporting hierarchy"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Regulatory Requirements"
                name="regulatoryRequirements"
                value={formData.regulatoryRequirements}
                onChange={handleChange}
                helperText="Any regulatory or compliance requirements"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Upload Charter Document (Optional)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={loading}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileUpload}
                  />
                </Button>
                {formData.charterDocument && (
                  <Typography variant="caption" sx={{ ml: 2 }}>
                    {formData.charterDocument.name}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              {isEditMode ? 'Save Changes' : 'Create Committee'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateCommittee;
